import React, { useEffect, useRef, useState } from "react";
import { http, API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Loader2, Plus, Trash2, Pencil, X, Upload, ImageIcon, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

function slugify(v) {
  return v
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

const imageSrc = (p) => (!p ? null : p.startsWith("http") ? p : `${API}/files/${p}`);

const EMPTY_FORM = { name: "", slug: "", description: "", active: true, image_url: "", sort_order: 0 };

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const imgInput = useRef(null);

  const load = () => {
    setLoading(true);
    http
      .get("/admin/categories")
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const open = (cat) => {
    if (cat) {
      setEditing(cat);
      setForm({
        name: cat.name,
        slug: cat.slug,
        description: cat.description || "",
        active: cat.active,
        image_url: cat.image_url || "",
        sort_order: cat.sort_order || 0,
      });
    } else {
      setEditing({});
      setForm(EMPTY_FORM);
    }
  };

  const close = () => { setEditing(null); };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/admin/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.storage_path }));
      toast.success("Imagen subida");
    } catch {
      toast.error("Error al subir imagen");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.name || !form.slug) {
      toast.error("Nombre y slug son requeridos");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        sort_order: Number(form.sort_order || 0),
      };
      if (editing?.category_id) {
        await http.put(`/admin/categories/${editing.category_id}`, payload);
        toast.success("Categoría actualizada");
      } else {
        await http.post("/admin/categories", payload);
        toast.success("Categoría creada");
      }
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await http.put(`/admin/categories/${c.category_id}`, {
        name: c.name,
        slug: c.slug,
        description: c.description || "",
        image_url: c.image_url || "",
        sort_order: c.sort_order || 0,
        active: !c.active,
      });
      toast.success(!c.active ? "Categoría visible en inicio" : "Categoría oculta del inicio");
      load();
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  const changeSort = async (c, dir) => {
    const newOrder = Math.max(0, (c.sort_order || 0) + dir);
    if (newOrder === (c.sort_order || 0)) return;
    try {
      await http.put(`/admin/categories/${c.category_id}`, {
        name: c.name,
        slug: c.slug,
        description: c.description || "",
        image_url: c.image_url || "",
        sort_order: newOrder,
        active: c.active,
      });
      load();
    } catch {
      toast.error("No se pudo reordenar");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar "${c.name}"?`)) return;
    try {
      await http.delete(`/admin/categories/${c.category_id}`);
      toast.success("Categoría eliminada");
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  // Sort visually for display: sort_order asc, then name
  const sorted = [...items].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name));
  const visibleOnHome = sorted.filter((c) => c.active);

  return (
    <div className="space-y-6" data-testid="admin-categories-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-mono uppercase text-neutral-500">/admin/categorías</div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tighter mt-1">Categorías</h2>
          <p className="text-xs text-neutral-500 mt-2 max-w-xl">
            Solo las categorías <strong>activas</strong> aparecen en la página de inicio y la tienda. Sube una imagen por categoría (se muestra como tarjeta en la home) y usa el orden para elegir cuál va primero.
          </p>
        </div>
        <Button onClick={() => open(null)} className="brutal-btn bg-black hover:bg-[#FF3B30] text-white h-10" data-testid="admin-add-category">
          <Plus className="w-4 h-4 mr-2" /> Nueva categoría
        </Button>
      </div>

      {/* Home preview */}
      {visibleOnHome.length > 0 && (
        <div className="brutal-card p-4" data-testid="admin-cat-home-preview">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs font-mono uppercase text-neutral-500">Vista previa · tarjetas de la portada</div>
              <div className="text-sm text-neutral-700 mt-1">Así se verán las categorías en <span className="font-mono">/</span>. La imagen que subes aquí es la que se muestra.</div>
            </div>
            <div className="text-[11px] font-mono uppercase text-neutral-500">{visibleOnHome.length} categorías visibles</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {visibleOnHome.map((c) => (
              <div
                key={c.category_id}
                className="relative aspect-[4/5] bg-neutral-100 border border-neutral-200 overflow-hidden group cursor-pointer hover:-translate-y-0.5 transition-transform"
                onClick={() => open(c)}
                data-testid={`admin-cat-home-tile-${c.slug}`}
                title="Click para editar esta categoría"
              >
                {c.image_url ? (
                  <img src={imageSrc(c.image_url)} alt={c.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] font-mono uppercase text-neutral-400 p-3 text-center">
                    Sin imagen — se usará la primera foto de un producto
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                  <div className="font-display text-sm text-white tracking-tighter truncate">{c.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="brutal-card overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
        ) : sorted.length === 0 ? (
          <div className="p-12 text-center text-neutral-500">Sin categorías. Crea la primera.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left">
                <th className="p-3 text-xs font-mono uppercase text-neutral-500 w-16">Imagen</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500">Nombre</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500">Slug</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500">Orden</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500">Visible</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr key={c.category_id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`admin-cat-row-${c.slug}`}>
                  <td className="p-3">
                    <div className="w-12 h-12 border border-neutral-200 bg-neutral-50 overflow-hidden">
                      {c.image_url ? (
                        <img src={imageSrc(c.image_url)} alt={c.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-neutral-300">
                          <ImageIcon className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="font-medium">{c.name}</div>
                    {c.description && <div className="text-xs text-neutral-500 line-clamp-1">{c.description}</div>}
                  </td>
                  <td className="p-3 font-mono text-xs">{c.slug}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => changeSort(c, -1)}
                        className="p-1 border border-neutral-200 hover:bg-neutral-100 disabled:opacity-30"
                        disabled={(c.sort_order || 0) === 0}
                        title="Subir en el orden"
                        data-testid={`admin-cat-order-up-${c.slug}`}
                      >
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <span className="text-xs font-mono px-2 min-w-[24px] text-center">{c.sort_order || 0}</span>
                      <button
                        type="button"
                        onClick={() => changeSort(c, 1)}
                        className="p-1 border border-neutral-200 hover:bg-neutral-100"
                        title="Bajar en el orden"
                        data-testid={`admin-cat-order-down-${c.slug}`}
                      >
                        <ArrowDown className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                  <td className="p-3">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-emerald-600"
                        checked={!!c.active}
                        onChange={() => toggleActive(c)}
                        data-testid={`admin-cat-toggle-${c.slug}`}
                      />
                      <span className={`text-[11px] font-mono uppercase ${c.active ? "text-emerald-700" : "text-neutral-400"}`}>
                        {c.active ? "En inicio" : "Oculta"}
                      </span>
                    </label>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => open(c)} data-testid={`admin-edit-cat-${c.slug}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-none h-8 border-red-400 text-red-600 hover:bg-red-50" onClick={() => remove(c)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex" data-testid="admin-cat-drawer">
          <div className="flex-1 bg-black/40" onClick={close} />
          <div className="w-full max-w-md bg-white h-full overflow-auto border-l border-neutral-200">
            <div className="sticky top-0 bg-white border-b border-neutral-200 p-4 flex items-center justify-between">
              <h3 className="font-display text-xl tracking-tight">{editing.category_id ? "Editar" : "Nueva"} categoría</h3>
              <button onClick={close} className="p-2 hover:bg-neutral-100" data-testid="admin-cat-drawer-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label className="text-xs font-mono uppercase">Nombre</Label>
                <Input
                  className="rounded-none mt-2"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || slugify(e.target.value) })}
                  data-testid="cat-form-name"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Slug (URL)</Label>
                <Input
                  className="rounded-none mt-2 font-mono"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                  data-testid="cat-form-slug"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Descripción</Label>
                <Textarea
                  className="rounded-none mt-2"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="cat-form-desc"
                />
              </div>

              {/* Image upload */}
              <div>
                <Label className="text-xs font-mono uppercase">Imagen de la categoría (aparece en la home)</Label>
                <div className="mt-2 flex items-start gap-3">
                  <div className="w-24 h-24 border border-neutral-200 bg-neutral-50 overflow-hidden flex-shrink-0" data-testid="cat-form-image-preview">
                    {form.image_url ? (
                      <img src={imageSrc(form.image_url)} alt="preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-300">
                        <ImageIcon className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      ref={imgInput}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => uploadImage(e.target.files?.[0])}
                      data-testid="cat-form-image-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-none h-9 w-full"
                      onClick={() => imgInput.current?.click()}
                      disabled={uploading}
                      data-testid="cat-form-image-upload"
                    >
                      {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {form.image_url ? "Cambiar imagen" : "Subir imagen"}
                    </Button>
                    {form.image_url && (
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-none h-8 w-full border-red-300 text-red-600 hover:bg-red-50 text-xs"
                        onClick={() => setForm((f) => ({ ...f, image_url: "" }))}
                        data-testid="cat-form-image-remove"
                      >
                        Quitar imagen
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-[11px] text-neutral-500 mt-2">Si no subes imagen, la home usa la primera foto de un producto de esta categoría como respaldo.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-mono uppercase">Orden</Label>
                  <Input
                    type="number"
                    min="0"
                    className="rounded-none mt-2"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                    data-testid="cat-form-sort"
                  />
                  <p className="text-[10px] text-neutral-500 mt-1 font-mono">Menor = primero</p>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm cursor-pointer h-10">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      className="w-4 h-4 accent-emerald-600"
                      data-testid="cat-form-active"
                    />
                    <span>Visible en la home</span>
                  </label>
                </div>
              </div>

              <Button onClick={save} disabled={saving} className="w-full brutal-btn bg-black hover:bg-[#FF3B30] text-white h-11" data-testid="cat-form-save">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Guardar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
